import React, { useState, useEffect, useMemo } from 'react';
import { Search, ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axiosInstance from '../api/axios';

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const Faq = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState(new Set());

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get('/faq')
      .then((res) => {
        if (mounted) setFaqs(res.data?.faqs || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return faqs;
    const terms = q.split(/\s+/);
    return faqs.filter((f) => {
      const hay = norm(f.question + ' ' + f.answer + ' ' + (f.category || ''));
      return terms.every((t) => hay.includes(t));
    });
  }, [faqs, query]);

  const grouped = useMemo(() => {
    const byCat = {};
    filtered.forEach((f) => {
      const key = f.category || 'Allgemein';
      if (!byCat[key]) byCat[key] = [];
      byCat[key].push(f);
    });
    return byCat;
  }, [filtered]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5EDE4' }}>
      <Navbar />
      <main className="flex-1 py-10 sm:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 mb-4 bg-white border border-[#E8DCC8]">
              <HelpCircle className="h-6 w-6 text-[#C4A084]" />
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl text-[#5D4E3C] mb-2"
              style={{ fontFamily: 'Georgia, serif' }}
              data-testid="faq-page-title"
            >
              Häufige Fragen
            </h1>
            <p className="text-sm text-[#8B7355]">
              Antworten auf die Fragen, die uns am meisten erreichen.
            </p>
          </div>

          {/* Suchfeld */}
          <div className="relative mb-8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B7355]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Suche z. B. „Versand", „Rückgabe", „Zahlung"…'
              className="w-full pl-10 pr-4 py-3 bg-white border border-[#E8DCC8] focus:border-[#C4A084] outline-none text-sm text-[#5D4E3C]"
              data-testid="faq-search-input"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B7355] hover:text-[#5D4E3C]"
              >
                Zurücksetzen
              </button>
            )}
          </div>

          {loading ? (
            <p className="text-center text-[#8B7355] py-8">Lädt…</p>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-[#E8DCC8] p-8 text-center">
              <p className="text-[#5D4E3C] font-medium mb-2">Keine Antwort gefunden.</p>
              <p className="text-sm text-[#8B7355] mb-4">
                Vielleicht findest du sie in einem anderen Wort — oder schreib uns direkt.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#C4A084] hover:bg-[#8B7355] text-white text-sm px-4 py-2 transition-colors"
              >
                <MessageCircle className="h-4 w-4" /> Frage stellen
              </Link>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(grouped).map(([cat, items]) => (
                <section key={cat}>
                  {Object.keys(grouped).length > 1 && (
                    <h2 className="text-[10px] uppercase tracking-widest text-[#8B7355] mb-2">
                      {cat}
                    </h2>
                  )}
                  <div className="bg-white border border-[#E8DCC8]">
                    {items.map((f, idx) => {
                      const isOpen = openIds.has(f.id);
                      return (
                        <div
                          key={f.id}
                          className={idx > 0 ? 'border-t border-[#E8DCC8]' : ''}
                          data-testid={`faq-item-${f.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => toggle(f.id)}
                            className="w-full flex items-start justify-between gap-3 text-left px-4 sm:px-6 py-4 hover:bg-[#FAF5EE] transition-colors"
                          >
                            <span className="font-semibold text-[#5D4E3C] pr-2">{f.question}</span>
                            <ChevronDown
                              className={`h-4 w-4 mt-1 flex-shrink-0 text-[#8B7355] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {isOpen && (
                            <div className="px-4 sm:px-6 pb-5 pt-1 text-sm text-[#5C4E3D] leading-relaxed whitespace-pre-line">
                              {f.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}

          {/* Kontakt-CTA */}
          <div className="mt-10 bg-gradient-to-br from-[#FAF5EE] to-[#F5EDE4] border border-[#E8DCC8] p-5 text-center">
            <p className="text-sm text-[#5D4E3C] mb-3">
              Deine Frage nicht dabei? Wir melden uns persönlich zurück.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#5D4E3C] hover:bg-[#C4A084] text-white text-xs uppercase tracking-widest px-5 py-2.5 transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Uns direkt schreiben
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Faq;
